import React, { useState, useEffect, useCallback } from 'react';
import {useNavigate} from 'react-router-dom';
import styled, {css} from 'styled-components';
import { DndContext, rectIntersection, PointerSensor, useSensor, useSensors, DragEndEvent, DragStartEvent, DragOverlay, Modifiers } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getListsByBoardId, getCardsByListId, createList, createCard, updateList, updateCard, deleteList, deleteCard, Board, CardList, Card, updateBoard } from '../../api';
import Input from '../common/Input';
import Button from '../common/Button';
import CardDetailModal from './CardDetailModal';
import useAuth from "../../hooks/useAuth";

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return isMobile;
};

// --- 스타일 컴포넌트 ---
const Container = styled.div`
  padding: 1rem;
  background-color: var(--bg-board);
  min-height: calc(100vh - 58px);
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  @media (min-width: 769px) {
    overflow-x: auto;
    white-space: nowrap;
  }
`;
const Header = styled.div`
  display: flex;
  flex-wrap: wrap; /* 화면이 좁아지면 줄바꿈 허용 */
  align-items: center;
  gap: 1rem; /* 요소 사이 간격 */
  margin-bottom: 1rem;
`;
const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1; /* 남는 공간 차지 */
`;
const BackButton = styled(Button)`
  margin-right: 1rem;
  background: var(--bg-list);
  &:hover { background: var(--border-color); }
`;
const BoardTitle = styled.h2`
  color: var(--text-bright);
  font-size: 1.5rem;
  cursor: pointer;
`;
const BoardTitleInput = styled(Input)`
  font-size: 1.5rem;
  font-weight: bold;
`;
const ListContainer = styled.div`
  display: inline-flex;
  gap: 1rem;
  align-items: flex-start;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    white-space: normal;
  }
`;
const ListWrapper = styled.div<{ isMobile?: boolean, isDragMode?: boolean }>`
  background-color: var(--bg-list);
  border-radius: 8px;
  width: 272px;
  flex-shrink: 0;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  touch-action: ${props => props.isMobile ? (props.isDragMode ? 'none' : 'auto') : 'auto'};

  @media (max-width: 768px) {
    width: 100%;
  }
`;
const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem; /* 요소 사이의 간격 추가 */
  padding: 0.5rem;
`;
const ListTitle = styled.h3`
  font-weight: bold;
  cursor: pointer;
  color: var(--text-bright);
  flex-grow: 1; /* 남는 공간을 모두 차지 */
  word-break: break-all; /* 긴 제목이 줄바꿈 되도록 */
`;
const ListTitleInput = styled(Input)`
  font-weight: bold;
  flex-grow: 1; /* 남는 공간을 모두 차지 */
  min-width: 0; /* flex 아이템이 줄어들 수 있도록 */
`;
const ListDeleteButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-dark);
  cursor: pointer;
  font-size: 1.2rem;
  line-height: 1;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  opacity: 0.6;
  flex-shrink: 0; /* 버튼이 찌그러지지 않도록 */
  &:hover {
    opacity: 1;
    background-color: var(--danger-accent);
    color: white;
  }
`;
const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 20px;
`;
const CardWrapper = styled.div<{ isCompleted?: boolean; status?: string | null }>`
  background-color: var(--bg-card);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  box-shadow: 0 1px 2px var(--shadow-light);
  cursor: pointer;
  white-space: normal;
  color: var(--text-normal);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  opacity: ${props => props.isCompleted ? 0.6 : 1};
  transition: all 0.2s ease-in-out;
  position: relative;

  &:hover {
    background-color: var(--border-color);
  }

  ${props => props.status === 'IN_PROGRESS' && css`
    border-width: 3px !important;
    border-style: solid !important;
    box-shadow: 0 0 0 1px var(--bg-card);
    animation: rainbow-border 4s linear infinite;

    @keyframes rainbow-border {
      0% {
        border-color: #ffffff;
      }
      17% {
        border-color: #ffdf00;
      }
      33% {
        border-color: #ffffff;
      }
      50% {
        border-color: #ffdf00;
      }
      67% {
        border-color: #ffffff;
      }
      83% {
        border-color: #ffdf00;
      }
      100% {
        border-color: #ffffff;
      }
    }
  `}
`;
const CardContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex-grow: 1;
`;
const CompleteCheckbox = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid var(--text-dark);
    border-radius: 50%;
    margin-top: 2px;
    cursor: pointer;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease-in-out;

    &:hover {
        border-color: #61afef;
    }
    
    &.completed {
        background-color: #98c379; /* 초록색 */
        border-color: #98c379;
    }
`;
const CheckIcon = styled.svg`
    width: 12px;
    height: 12px;
    stroke: white;
    stroke-width: 2;
`;
const CardLabelsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
`;
const CardLabelPill = styled.div<{ color: string }>`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
  color: white;
  background-color: ${props => props.color};
`;
const AddCardButton = styled(Button)`
  color: var(--text-normal);
  &:hover { background-color: var(--border-color); }
`;
const AddControls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;
const ListCreatorWrapper = styled.div`
  background-color: var(--bg-list);
  border-radius: 8px;
  width: 272px;
  flex-shrink: 0;
  padding: 1rem;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  @media (max-width: 768px) {
    width: 100%;
  }
`;
const SearchInput = styled(Input)`
    margin-left: auto; /* 오른쪽으로 밀어냄 */
    width: 250px;
`;
const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%; /* 모바일에서 한 줄 차지 */
  @media (min-width: 769px) {
    width: auto; /* 데스크탑에서는 원래 크기 */
    margin-left: auto;
  }
`;
const SearchSelect = styled.select`
    background-color: var(--bg-input);
    color: var(--text-bright);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    padding: 0.5rem;
    font-size: 0.9rem;
`;
const CardTitle = styled.span<{ isCompleted?: boolean }>`
  text-decoration: ${props => props.isCompleted ? 'line-through' : 'none'};
`;
const CardMetaContainer = styled.div`
    font-size: 0.8rem;
    color: var(--text-dark);
`;
const FloatingActionButton = styled.button`
    position: fixed;
    bottom: 30px;
    right: 30px;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: var(--primary-accent);
    color: white;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 10px var(--shadow-strong);
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;
    z-index: 5;

    &:hover {
        background-color: var(--primary-accent-hover);
    }
  
    svg {
        width: 32px;
        height: 32px;
    }
`;

const DragModeToggleButton = styled.button<{ isActive: boolean }>`
  position: fixed;
  bottom: 100px;
  right: 30px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${props => props.isActive ? 'var(--primary-accent)' : 'var(--bg-list)'};
  color: var(--text-bright);
  border: 1px solid var(--border-color);
  display: none; // 기본적으로 숨김
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px var(--shadow-strong);
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  z-index: 5;

  @media (max-width: 768px) {
    display: flex; // 모바일에서만 보임
  }

  &:hover {
    background-color: ${props => props.isActive ? 'var(--primary-accent-hover)' : 'var(--border-color)'};
  }

  svg {
    width: 32px;
    height: 32px;
  }
`;
const SortableListWrapper = styled.div`
  @media (max-width: 768px) {
    width: 100%;
  }
`;

interface BoardDetailProps {
    selectedBoard: Board;
    onBack: () => void;
    onBoardUpdate: (updatedBoard: Board) => void;
    onListsUpdate: (lists: ListWithCards[]) => void;
    isOutboxOpen: boolean;
    onRefreshNeeded: () => void;
}
interface SortableCardProps {
    card: Card;
    onClick: () => void;
    onToggleComplete: () => void;
    disabled?: boolean;
}
export interface ListWithCards extends CardList {
    cards: Card[];
}
interface SortableListProps {
    list: ListWithCards;
    children: React.ReactNode;
    disabled?: boolean;
    isMobile?: boolean;
    isDragMode?: boolean;
}
interface VirtualDropZoneProps {
    id: string;
}
const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    // 시간 부분을 제외하고 'YYYY-MM-DD' 형식으로 반환
    return dateString.split(' ')[0];
};
const SortableCard: React.FC<SortableCardProps> = ({ card, onClick, onToggleComplete, disabled }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: card.id, disabled });
    const style = { transform: CSS.Transform.toString(transform), transition };

    const startDate = formatDate(card.startDate);
    const dueDate = formatDate(card.dueDate);

    const handleCheckboxClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // 카드 상세 모달이 열리는 것을 방지
        onToggleComplete();
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <CardWrapper onClick={onClick} isCompleted={card.completed ?? false} status={card.status}>
                <CompleteCheckbox
                    className={card.completed ? 'completed' : ''}
                    onClick={handleCheckboxClick}
                >
                    {card.completed && <CheckIcon viewBox="0 0 24 24"><path fill="none" d="M20 6L9 17l-5-5"/></CheckIcon>}
                </CompleteCheckbox>
                <CardContent>
                    {card.labels && card.labels.length > 0 && (
                        <CardLabelsContainer>
                            {card.labels.map(label => (
                                <CardLabelPill key={label.name} color={label.color}>
                                    {label.name}
                                </CardLabelPill>
                            ))}
                        </CardLabelsContainer>
                    )}
                    <CardTitle isCompleted={card.completed ?? false}>{card.title}</CardTitle>
                    {(startDate || dueDate) && (
                        <CardMetaContainer>
                            <span>{startDate || '...'} ~ {dueDate || '...'}</span>
                        </CardMetaContainer>
                    )}
                </CardContent>
            </CardWrapper>
        </div>
    );
};
const SortableList: React.FC<SortableListProps> = ({ list, children, disabled, isMobile, isDragMode }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: list.id, disabled });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return <SortableListWrapper ref={setNodeRef} style={style}><ListWrapper {...attributes} {...listeners} isMobile={isMobile} isDragMode={isDragMode}>{children}</ListWrapper></SortableListWrapper>;
};
const VirtualDropZone: React.FC<VirtualDropZoneProps> = ({ id }) => {
    const { setNodeRef } = useSortable({ id });
    return <div ref={setNodeRef} style={{ height: '1px', opacity: 0, pointerEvents: 'none' }} />;
};


// --- BoardDetail 메인 컴포넌트 ---
const BoardDetail: React.FC<BoardDetailProps> = ({ selectedBoard, onBack, onBoardUpdate, onListsUpdate, isOutboxOpen, onRefreshNeeded}) => {
    const navigate = useNavigate();
    const [lists, setLists] = useState<ListWithCards[]>([]);
    const [newListName, setNewListName] = useState('');
    const [viewingCard, setViewingCard] = useState<Card | null>(null);
    const [addingCardToListId, setAddingCardToListId] = useState<string | null>(null);
    const [newCardTitle, setNewCardTitle] = useState('');
    const [activeList, setActiveList] = useState<ListWithCards | null>(null);
    const [activeCard, setActiveCard] = useState<Card | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingValue, setEditingValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [searchType, setSearchType] = useState<'title' | 'description'>('title');

    const [isDragModeEnabled, setIsDragModeEnabled] = useState(false);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const isMobile = useIsMobile();

    const user = useAuth();
    const isReadOnly = viewingCard?.userId !== user?.id && viewingCard?.assigneeId !== user?.id;

    const handleToggleOutbox = () => {
        if (isOutboxOpen) {
            navigate(`/board/${selectedBoard.id}`); // 열려있으면 닫기
        } else {
            navigate(`/board/${selectedBoard.id}/outbox`); // 닫혀있으면 열기
        }
    };

    const modifiers: Modifiers = [
        ({ transform }) => {
            if (isOutboxOpen) {
                return {
                    ...transform,
                    x: transform.x - 350, // 사이드바 너비만큼 왼쪽으로 이동
                };
            }
            return transform;
        },
    ];

    const fetchListsAndCards = useCallback(async () => {
        try {
            const listResponse = await getListsByBoardId(selectedBoard.id);
            const listsData = listResponse.data?._embedded?.getList || [];
            const listsWithCards = await Promise.all(
                listsData.map(async (list) => {
                    const searchParams: { title?: string; description?: string; completed?: boolean } = {
                        completed: false,
                    };
                    if (debouncedSearchTerm) {
                        searchParams[searchType] = debouncedSearchTerm;
                    }
                    const cardResponse = await getCardsByListId(selectedBoard.id, list.id, searchParams);
                    const cardsData = cardResponse.data?._embedded?.getList || [];

                    cardsData.sort((a, b) => {
                        const aCompleted = a.completed ?? false;
                        const bCompleted = b.completed ?? false;
                        if (aCompleted === bCompleted) return a.priority - b.priority;
                        return aCompleted ? 1 : -1;
                    });

                    return { ...list, cards: cardsData };
                })
            );
            setLists(listsWithCards);
            onListsUpdate(listsWithCards);
        } catch (error) { console.error('Failed to fetch lists and cards', error); }
    }, [selectedBoard.id, debouncedSearchTerm, searchType, onListsUpdate]);

    useEffect(() => { fetchListsAndCards(); }, [fetchListsAndCards]);

    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 100);
        return () => {
            clearTimeout(timerId);
        };
    }, [searchTerm]);

    const handleCreateList = async () => {
        if (!newListName.trim()) return;
        try {
            await createList({ name: newListName, boardId: selectedBoard.id, priority: 0 });
            setNewListName('');
            fetchListsAndCards();
        } catch (error) { console.error('Failed to create list', error); }
    };

    const handleCreateCard = async (listId: string) => {
        if (!newCardTitle.trim()) return;
        try {
            await createCard({ title: newCardTitle, boardId: selectedBoard.id, cardListId: listId, priority: 0 });
            setNewCardTitle('');
            setAddingCardToListId(null);
            fetchListsAndCards();
        } catch (error) { console.error('Failed to create card', error); }
    };

    const handleViewCardDetails = (cardId: string) => {
        navigate(`card/${cardId}`);
    };

    const handleCardUpdate = (updatedCard: Card) => {
        setLists(prevLists => prevLists.map(list => {
            const cardIndex = list.cards.findIndex(c => c.id === updatedCard.id);
            if (cardIndex > -1) {
                const newCards = [...list.cards];
                newCards[cardIndex] = updatedCard;
                return { ...list, cards: newCards };
            }
            return list;
        }));
        if (viewingCard && viewingCard.id === updatedCard.id) {
            setViewingCard(updatedCard);
        }
    };

    const handleToggleComplete = async (cardToUpdate: Card) => {
        try {
            await updateCard({ id: cardToUpdate.id, completed: !cardToUpdate.completed });
            onRefreshNeeded();
        } catch (error: any) {
            console.error("Failed to update card completion status", error);
            onRefreshNeeded();
            const message = error.response?.data?.data || "완료 처리 실패";
            alert(message);
        }
    };

    const handleDeleteCard = async (cardId: string) => {
        try {
            await deleteCard(cardId);
            setViewingCard(null);
            fetchListsAndCards();
        } catch (error) { console.error('Failed to delete card', error); alert('Failed to delete card.'); }
    };

    const handleDeleteList = async (listId: string) => {
        if (window.confirm('Are you sure you want to delete this list? All cards within it will also be deleted.')) {
            try {
                await deleteList(listId);
                fetchListsAndCards();
            } catch (error) {
                console.error('Failed to delete list', error);
                alert('Failed to delete list.');
            }
        }
    };

    const handleTitleClick = (id: string, currentValue: string) => {
        setEditingId(id);
        setEditingValue(currentValue);
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEditingValue(e.target.value);
    };

    const handleTitleSave = async () => {
        if (!editingId || !editingValue.trim()) {
            setEditingId(null);
            return;
        }

        if (editingId === selectedBoard.id) {
            if (editingValue !== selectedBoard.name) {
                try {
                    const updatedBoard = { ...selectedBoard, name: editingValue };
                    await updateBoard(updatedBoard);
                    onBoardUpdate(updatedBoard);
                } catch (error) { console.error('Failed to update board title', error); }
            }
        } else {
            const listToUpdate = lists.find(l => l.id === editingId);
            if (listToUpdate && editingValue !== listToUpdate.name) {
                try {
                    const updatedList = { ...listToUpdate, name: editingValue };
                    await updateList(updatedList);
                    setLists(prev => prev.map(l => l.id === editingId ? updatedList : l));
                } catch (error) { console.error('Failed to update list title', error); }
            }
        }
        setEditingId(null);
    };

    const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleTitleSave();
        if (e.key === 'Escape') setEditingId(null);
    };

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        const activeId = active.id.toString();
        const list = lists.find(l => l.id === activeId);
        if (list) { setActiveList(list); return; }
        const card = lists.flatMap(l => l.cards).find(c => c.id === activeId);
        if (card) { setActiveCard(card); }
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        setActiveList(null);
        setActiveCard(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const activeId = active.id.toString();
        const overId = over.id.toString();
        const isListDrag = lists.some(l => l.id === activeId);

        if (isListDrag) {
            const oldIndex = lists.findIndex(l => l.id === activeId);
            const newIndex = lists.findIndex(l => l.id === overId);
            if (oldIndex === -1 || newIndex === -1) return;
            const reorderedLists = arrayMove(lists, oldIndex, newIndex);
            setLists(reorderedLists);
            const updatePromises = reorderedLists.map((list, index) => updateList({ id: list.id, priority: index }));
            try { await Promise.all(updatePromises); }
            catch (error) { console.error('Failed to update list priorities', error); fetchListsAndCards(); }
        } else {
            let destinationList: ListWithCards | undefined;
            if (overId.startsWith('dropzone-')) {
                destinationList = lists.find(l => l.id === overId.replace('dropzone-', ''));
            } else {
                destinationList = lists.find(l => l.id === overId || l.cards.some(c => c.id === overId));
            }

            const sourceList = lists.find(l => l.cards.some(c => c.id === activeId));

            if (sourceList && destinationList) {
                if (sourceList.id === destinationList.id) {
                    const oldIndex = sourceList.cards.findIndex(c => c.id === activeId);
                    const newIndex = destinationList.cards.findIndex(c => c.id === overId);
                    if (oldIndex !== -1 && newIndex !== -1) {
                        const reorderedCards = arrayMove(sourceList.cards, oldIndex, newIndex);
                        setLists(lists.map(l => l.id === sourceList.id ? { ...l, cards: reorderedCards } : l));
                        const updatePromises = reorderedCards.map((card, index) => updateCard({ id: card.id, priority: index }));
                        try { await Promise.all(updatePromises); }
                        catch (error) { console.error('Failed to update card priorities', error); fetchListsAndCards(); }
                    }
                } else {
                    let movedCard: Card;
                    const newLists = [...lists];
                    const sourceListIndex = newLists.findIndex(l => l.id === sourceList.id);
                    // @ts-ignore
                    const destListIndex = newLists.findIndex(l => l.id === destinationList.id);

                    if(sourceListIndex === -1 || destListIndex === -1) return;

                    const activeCardIndex = sourceList.cards.findIndex(c => c.id === activeId);
                    [movedCard] = newLists[sourceListIndex].cards.splice(activeCardIndex, 1);

                    let overCardIndex = destinationList.cards.findIndex(c => c.id === overId);
                    if (overCardIndex === -1) overCardIndex = destinationList.cards.length;
                    newLists[destListIndex].cards.splice(overCardIndex, 0, movedCard);
                    setLists(newLists);

                    try {
                        await updateCard({ id: movedCard.id, cardListId: destinationList.id });
                        const sourcePromises = newLists[sourceListIndex].cards.map((card, index) => updateCard({ id: card.id, priority: index }));
                        const destPromises = newLists[destListIndex].cards.map((card, index) => updateCard({ id: card.id, priority: index }));
                        await Promise.all([...sourcePromises, ...destPromises]);
                    } catch (error) {
                        console.error('Failed to update card priorities after move', error);
                        fetchListsAndCards();
                    }
                }
            }
        }
    };

    return (
        <Container>
            <Header>
                <HeaderLeft>
                    <BackButton onClick={onBack}>← Back to Boards</BackButton>
                    {editingId === selectedBoard.id ? (
                        <BoardTitleInput value={editingValue} onChange={handleTitleChange} onBlur={handleTitleSave} onKeyDown={handleTitleKeyDown} autoFocus />
                    ) : (
                        <BoardTitle onClick={() => handleTitleClick(selectedBoard.id, selectedBoard.name)}>{selectedBoard.name}</BoardTitle>
                    )}
                </HeaderLeft>
                <SearchWrapper>
                    <SearchSelect value={searchType} onChange={(e) => setSearchType(e.target.value as 'title' | 'description')}>
                        <option value="title">Title</option>
                        <option value="description">Description</option>
                    </SearchSelect>
                    <SearchInput
                        type="text"
                        placeholder="Search cards..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </SearchWrapper>
            </Header>
            <DndContext
                sensors={sensors}
                collisionDetection={rectIntersection}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <ListContainer>
                    <SortableContext items={lists.map(l => l.id)} strategy={horizontalListSortingStrategy}>
                        {lists.map(list => {
                            const isDisabled = isMobile && !isDragModeEnabled;
                            return (
                            <SortableList key={list.id} list={list} disabled={isDisabled} isMobile={isMobile} isDragMode={isDragModeEnabled}>
                                <ListHeader>
                                    {editingId === list.id ? (
                                        <ListTitleInput value={editingValue} onChange={handleTitleChange} onBlur={handleTitleSave} onKeyDown={handleTitleKeyDown} autoFocus />
                                    ) : (
                                        <ListTitle onClick={() => handleTitleClick(list.id, list.name)}>{list.name}</ListTitle>
                                    )}
                                    <ListDeleteButton onClick={() => handleDeleteList(list.id)}>×</ListDeleteButton>
                                </ListHeader>
                                <SortableContext id={list.id} items={list.cards.length > 0 ? list.cards.map(c => c.id) : [`dropzone-${list.id}`]} strategy={verticalListSortingStrategy}>
                                    <CardsContainer>
                                        {list.cards.map(card => <SortableCard key={card.id} card={card} onClick={() => handleViewCardDetails(card.id)} onToggleComplete={() => handleToggleComplete(card)} disabled={isDisabled} />)}
                                        {list.cards.length === 0 && <VirtualDropZone id={`dropzone-${list.id}`} />}
                                    </CardsContainer>
                                </SortableContext>
                                {addingCardToListId === list.id ? (
                                    <div>
                                        <Input value={newCardTitle} onChange={(e) => setNewCardTitle(e.target.value)} placeholder="Enter card title..." autoFocus/>
                                        <AddControls>
                                            <Button onClick={() => handleCreateCard(list.id)}>Add Card</Button>
                                            <Button onClick={() => setAddingCardToListId(null)} style={{background: 'transparent', color: '#5e6c84'}}>X</Button>
                                        </AddControls>
                                    </div>
                                ) : (
                                    <AddCardButton onClick={() => setAddingCardToListId(list.id)}>+ Add a card</AddCardButton>
                                )}
                            </SortableList>
                        )
                        })}
                    </SortableContext>
                    <ListCreatorWrapper>
                        <Input value={newListName} onChange={(e) => setNewListName(e.target.value)} placeholder="+ Add another list" />
                        <Button onClick={handleCreateList} style={{marginTop: '0.5rem'}}>Add List</Button>
                    </ListCreatorWrapper>
                </ListContainer>

                <DragOverlay modifiers={modifiers}>
                    {activeList ? (
                        <ListWrapper>
                            <ListHeader>
                                <ListTitle>{activeList.name}</ListTitle>
                            </ListHeader>
                            <CardsContainer>
                                {
                                    activeList.cards.map(card =>
                                    <CardWrapper isCompleted={card.completed ?? false} style={{ width: '272px' }}>
                                        <CompleteCheckbox className={card.completed ? 'completed' : ''}>
                                            {card.completed && <CheckIcon viewBox="0 0 24 24"><path fill="none" d="M20 6L9 17l-5-5"/></CheckIcon>}
                                        </CompleteCheckbox>
                                        <CardContent>
                                            {card.labels && card.labels.length > 0 && (
                                                <CardLabelsContainer>
                                                    {card.labels.map(label =>
                                                        <CardLabelPill key={label.name} color={label.color}>
                                                            {label.name}
                                                        </CardLabelPill>
                                                    )}
                                                </CardLabelsContainer>
                                            )}
                                            <CardTitle isCompleted={card.completed ?? false}>{card.title}</CardTitle>
                                            {(card.startDate || card.dueDate) && (
                                                <CardMetaContainer>
                                                    <span>
                                                        {formatDate(card.startDate) || '...'} ~ {formatDate(card.dueDate) || '...'}
                                                    </span>
                                                    </CardMetaContainer>
                                                )}
                                            </CardContent>
                                        </CardWrapper>
                                    )}
                            </CardsContainer>
                        </ListWrapper>
                    ) : null}
                    {activeCard ? (
                        <CardWrapper isCompleted={activeCard.completed ?? false} style={{ width: '272px' }}>
                            <CompleteCheckbox className={activeCard.completed ? 'completed' : ''}>
                                {activeCard.completed && <CheckIcon viewBox="0 0 24 24"><path fill="none" d="M20 6L9 17l-5-5"/></CheckIcon>}
                            </CompleteCheckbox>
                            <CardContent>
                                {activeCard.labels && activeCard.labels.length > 0 && (
                                    <CardLabelsContainer>
                                        {activeCard.labels.map(label =>
                                            <CardLabelPill key={label.name} color={label.color}>
                                                {label.name}
                                            </CardLabelPill>
                                        )}
                                    </CardLabelsContainer>
                                )}
                                <CardTitle isCompleted={activeCard.completed ?? false}>{activeCard.title}</CardTitle>
                                {(activeCard.startDate || activeCard.dueDate) && (
                                    <CardMetaContainer>
                                        <span>{formatDate(activeCard.startDate) || '...'} ~ {formatDate(activeCard.dueDate) || '...'}</span>
                                    </CardMetaContainer>
                                )}
                            </CardContent>
                        </CardWrapper>
                    ) : null}
                </DragOverlay>
            </DndContext>
            {viewingCard && (
                <CardDetailModal readOnly={isReadOnly} card={viewingCard} onClose={() => setViewingCard(null)} onUpdate={handleCardUpdate} onDelete={handleDeleteCard} onToggleComplete={() => handleToggleComplete(viewingCard)} fromBoard={true} />
            )}

            <DragModeToggleButton
                isActive={isDragModeEnabled}
                onClick={() => setIsDragModeEnabled(prev => !prev)}
                title={isDragModeEnabled ? "Switch to Scroll Mode" : "Switch to Drag Mode"}
            >
                {isDragModeEnabled ? (
                    // 드래그 모드 활성화 시 (스크롤 모드로 돌아가는 아이콘)
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                ) : (
                    // 스크롤 모드 활성화 시 (드래그 모드로 전환하는 아이콘)
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20"/>
                    </svg>
                )}
            </DragModeToggleButton>

            <FloatingActionButton onClick={handleToggleOutbox} title="View Outbox">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
            </FloatingActionButton>
        </Container>
    );
};

export default BoardDetail;